import SearchIcon from '@mui/icons-material/Search';
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { UpdateForm } from 'lib/api/sgr/types';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import { isAdmin } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import BusinessUnitsDialog from '../Common/BusinessUnitsDialog';

type Props = {
  form: UpdateForm;
  setForm: (form: UpdateForm) => void;
};

const UpdateWarrantyForm: React.FC<Props> = ({ form, setForm }) => {
  const auth = useSelector((state: RootState) => state.auth);
  const userIsAdmin = auth.user ? isAdmin(auth.user) : false;
  const [open, setOpen] = useState(false);

  const updateField = (updates: Partial<UpdateForm>) => {
    setForm({ ...form, ...updates });
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="ID Licitación"
            value={form?.tenderId}
            onChange={(e) => updateField({ tenderId: e.target.value })}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Tipo de Garantía"
            value={form.type}
            onChange={(e) => updateField({ type: e.target.value })}
            required
          >
            {SGR_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Monto Cotizado"
            type="number"
            value={form.quotedAmount}
            onChange={(e) =>
              updateField({ quotedAmount: Number(e.target.value) })
            }
            inputProps={{ step: 'any' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Select
                    value={form.quotedCurrency}
                    onChange={(e) =>
                      updateField({ quotedCurrency: e.target.value })
                    }
                    variant="standard"
                  >
                    {CURRENCIES.map((currency) => (
                      <MenuItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              ),
            }}
            required
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="fogape-select-label">Fogape</InputLabel>
            <Select
              labelId="fogape-select-label"
              label="Fogape"
              value={form.fogape}
              onChange={(e) => updateField({ fogape: Number(e.target.value) })}
            >
              {Array.from({ length: 11 }, (_, i) => (
                <MenuItem key={i * 10} value={i * 10}>
                  {i * 10}%
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha Inicio"
              value={form.startDate}
              onChange={(date) => updateField({ startDate: date })}
              minDate={userIsAdmin ? undefined : dayjs().subtract(7, 'day')}
              maxDate={userIsAdmin ? undefined : dayjs().add(7, 'day')}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { fullWidth: true, required: true },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha Término"
              value={form.endDate}
              onChange={(date) => updateField({ endDate: date })}
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="RUT Mandante"
            value={form.principalRut}
            onChange={(e) => updateField({ principalRut: e.target.value })}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setOpen(true)}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Unidad de Negocio"
            value={form.unitCode ?? ''}
            onChange={(e) => updateField({ unitCode: e.target.value })}
            InputLabelProps={{ shrink: Boolean(form.unitCode) }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Dirección Mandante"
            value={form.address ?? ''}
            onChange={(e) => updateField({ address: e.target.value })}
            InputLabelProps={{ shrink: Boolean(form.address) }}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Glosa"
            value={form.description}
            onChange={(e) => updateField({ description: e.target.value })}
            multiline
            minRows={3}
            maxRows={3}
          />
        </Grid>
      </Grid>
      {open && (
        <BusinessUnitsDialog
          open={open}
          onClose={() => setOpen(false)}
          form={form}
          setForm={setForm}
          canEditAddress
        />
      )}
    </>
  );
};

export default UpdateWarrantyForm;
